import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Grid,
    Input
} from "@mui/material"
import {
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { ButtonStyles, DialogStyles } from "../styles/MaintenanceStyles"
import {
    createFrequency,
    getFrequencies,
    deleteFrequency,
    updateFrequency,
    updateselected,
    setAlertDialog,
    setAlertText
} from "../../../actions/maintenanceActions"
import axios from "../../../auth/axiosAuth"
import { backendURL } from "../../../config"

function FrequencyToolBar() {
    const dispatch = useDispatch()

    // Store Variables
    const maintenance = useSelector((state) => state.maintenance)
    const auth = useSelector((state) => state.auth)
    const permissions = useSelector((state) => state.auth.permissions)

    // Add Dialog State
    const [addDialogValues, setAddDialogValues] = useState({
        frequency: ""
    })
    const [addDialogOpen, setAddDialogOpen] = useState(false)

    //edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editDialogValues, setEditDialogValues] = useState({
        frequency: ""
    })

    // Delete Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    // Alert Handlers
    const handleAlertDialogClose = (event, reason) => {
        if (reason === "clickaway") {
            return
        }
        dispatch(setAlertDialog(false))
    }

    // Add Dialog Handlers
    const handleAddDialogOpen = () => {
        clearAddDialogValues()
        setAddDialogOpen(true)
    }

    const handleAddDialogClose = () => {
        clearAddDialogValues()
        setAddDialogOpen(false)
        handleAlertDialogClose(null, null)
    }

    const clearAddDialogValues = () => {
        setAddDialogValues({ frequency: "" })
    }

    const handleConfirmAdd = async () => {
        await dispatch(getFrequencies())
        if (addDialogValues.frequency !== "") {
            if (
                maintenance.frequencies?.filter(
                    (frequency) =>
                        frequency.Frequency.toLowerCase() ===
                        addDialogValues.frequency.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This frequency already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    addDialogValues.frequency = addDialogValues.frequency.trim()
                    const newFrequency = {
                        Frequency: addDialogValues.frequency,
                        AuditLog: {
                            Action: "Create Frequency",
                            OldValue: "",
                            NewValue: addDialogValues.frequency,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(createFrequency(newFrequency))
                    await dispatch(getFrequencies())
                    setAddDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(setAlertText("Frequency Name field must be filled out"))
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateAddDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setAddDialogValues({
            ...addDialogValues,
            [name]: value
        })
    }

    // Delete Dialog Handlers
    const handleDeleteDialogOpen = async () => {
        const res = await axios.get(
            `${backendURL}/frequencies/check/${maintenance.selected.FrequencyId}`
        )
        if (res.data.length > 0) {
            dispatch(
                setAlertText(
                    "This frequency is being used and cannot be deleted"
                )
            )
            dispatch(setAlertDialog(true))
            return
        }
        if (maintenance.selected.FrequencyId) {
            setAddDialogValues({
                frequency: maintenance.selected.Frequency
            })
            setDeleteDialogOpen(true)
        }
    }

    const handleDeleteDialogClose = () => {
        clearAddDialogValues()
        setDeleteDialogOpen(false)
    }

    const handleConfirmDelete = async () => {
        try {
            const toDeleteFrequency = {
                FrequencyId: maintenance.selected.FrequencyId,
                AuditLog: {
                    Action: "Delete Frequency",
                    OldValue: maintenance.selected.Frequency,
                    NewValue: "",
                    AssociateOID: auth.userData.AssociateOID
                }
            }
            await dispatch(deleteFrequency(toDeleteFrequency))
            await dispatch(getFrequencies())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        } catch (error) {
            await dispatch(getFrequencies())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        }
    }

    //Edit Dialog Handlers
    const handleEditDialogOpen = async () => {
        const res = await axios.get(
            `${backendURL}/frequencies/check/${maintenance.selected.FrequencyId}`
        )
        if (res.data.length > 0) {
            dispatch(
                setAlertText(
                    "This frequency is being used and cannot be updated"
                )
            )
            dispatch(setAlertDialog(true))
            return
        }
        if (maintenance.selected.FrequencyId) {
            setEditDialogValues({
                frequency: maintenance.selected.Frequency
            })
            setEditDialogOpen(true)
        }
    }

    const handleEditDialogClose = () => {
        clearAddDialogValues()
        setEditDialogOpen(false)
        handleAlertDialogClose(null, null)
    }

    const handleConfirmEdit = async () => {
        if (editDialogValues.frequency !== "") {
            if (
                maintenance.frequencies?.filter(
                    (frequency) =>
                        frequency.Frequency.toLowerCase() ===
                        editDialogValues.frequency.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This frequency already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    editDialogValues.frequency =
                        editDialogValues.frequency.trim()
                    const updatedFrequency = {
                        FrequencyId: maintenance.selected.FrequencyId,
                        Frequency: editDialogValues.frequency
                    }
                    const newFrequency = {
                        Frequency: updatedFrequency,
                        AuditLog: {
                            Action: "Update Frequency",
                            OldValue: maintenance.selected.Frequency,
                            NewValue: editDialogValues.frequency,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(updateFrequency(newFrequency))
                    dispatch(updateselected([updatedFrequency]))
                    await dispatch(getFrequencies())
                    setEditDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(setAlertText("Frequency Name field must be filled out"))
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateEditDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setEditDialogValues({
            ...editDialogValues,
            [name]: value
        })
    }

    return (
        <Grid>
            {/*ToolBar*/}
            <GridToolbarContainer sx={ButtonStyles}>
                <GridToolbarFilterButton sx={ButtonStyles} />
                <GridToolbarDensitySelector sx={ButtonStyles} />
                <GridToolbarExport sx={ButtonStyles} />
                {permissions.EditMaintenance && (
                    <>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddDialogOpen}
                            sx={ButtonStyles}
                            disabled={maintenance.isBeingEdited}
                        >
                            Add
                        </Button>
                        {maintenance.selected.FrequencyId && (
                            <>
                                <Button
                                    startIcon={<EditIcon />}
                                    onClick={handleEditDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Edit
                                </Button>
                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </>
                )}
            </GridToolbarContainer>

            {/*Add frequency Dialog*/}
            <Dialog
                open={addDialogOpen}
                onClose={handleAddDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Add Frequency</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Please enter the frequency you would like to add
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Frequency Name"
                        onChange={handleUpdateAddDialogValues}
                        name="frequency"
                        sx={DialogStyles}
                        value={addDialogValues.frequency}
                        fullWidth
                        variant="standard"
                        placeholder="Frequency Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmAdd}
                        sx={DialogStyles}
                    >
                        Add
                    </Button>
                    <Button
                        onClick={handleAddDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Delete Frequency Dialog*/}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Delete Frequency</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Are you sure you would like to delete{" "}
                        {addDialogValues.frequency}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmDelete}
                        sx={DialogStyles}
                    >
                        Yes
                    </Button>
                    <Button
                        onClick={handleDeleteDialogClose}
                        sx={DialogStyles}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Edit frequency Dialog*/}
            <Dialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Edit Frequency</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        What would you like to change the frequency to?
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Frequency Name"
                        onChange={handleUpdateEditDialogValues}
                        name="frequency"
                        sx={DialogStyles}
                        value={editDialogValues.frequency}
                        fullWidth
                        variant="standard"
                        placeholder="Frequency Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmEdit}
                        sx={DialogStyles}
                    >
                        Confirm
                    </Button>
                    <Button
                        onClick={handleEditDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default FrequencyToolBar
